import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { bdApi } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { GripVertical, Plus, TrendingUp, Users, AlertCircle, Award, Target, ArrowRight, Trash2 } from "lucide-react";

export const CATEGORY_LABELS = {
  employer_deal: "Employer Deal",
  grant: "Grant",
  government_contract: "Government Contract",
  foundation: "Foundation",
  corporate_sponsorship: "Corporate Sponsorship",
  strategic_partnership: "Strategic Partnership",
  community_partnership: "Community Partnership",
  volunteer_engagement: "Volunteer Engagement",
  donation: "Donation",
  other: "Other",
};

const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

function money(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 1_000_000) return `$${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)    return `$${(n/1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

// ═════════════════════ KANBAN BOARD ═════════════════════
export function BDKanban({ orgId }) {
  const [category, setCategory] = useState("employer_deal");
  const [board, setBoard] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [dragging, setDragging] = useState(null);

  const load = () => bdApi.kanban({ category }).then(setBoard).catch(()=>setBoard(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const onDrop = async (targetStage) => {
    if (!dragging || dragging.stage === targetStage) { setDragging(null); return; }
    try {
      await bdApi.transitionOppStage(dragging.id, targetStage);
      toast.success(`Moved "${dragging.title.slice(0,40)}" → ${board.pipeline.stages.find(s=>s.key===targetStage)?.label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to move"); }
    finally { setDragging(null); }
  };

  return (
    <div data-testid="bd-kanban-view">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <Label className="text-xs">Pipeline</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-64 mt-1" data-testid="kanban-category-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          {board && <div className="text-xs text-slate-600">{board.total_opps} opps · <span className="font-semibold text-slate-900">{money(board.total_value)}</span> in pipeline</div>}
          <Button className="cod-bg-red" onClick={()=>setShowCreate(true)} data-testid="btn-new-opp"><Plus size={14} className="mr-1"/> New Opportunity</Button>
        </div>
      </div>

      {!board && <div className="text-sm text-slate-500">Loading…</div>}
      {board && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max" data-testid="kanban-columns">
            {board.pipeline.stages.map(stage => (
              <div
                key={stage.key}
                onDragOver={e => e.preventDefault()}
                onDrop={() => onDrop(stage.key)}
                className={`w-72 flex-shrink-0 rounded-lg border ${dragging ? "border-[var(--cod-red)] border-dashed" : "border-slate-200"} bg-slate-50/50`}
                data-testid={`kanban-col-${stage.key}`}
              >
                <div className="px-3 py-2 border-b border-slate-200 bg-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest font-semibold text-slate-700">{stage.label}</div>
                    <div className="text-xs text-slate-500">{(board.columns[stage.key] || []).length}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {money((board.columns[stage.key] || []).reduce((sum, o)=>sum + (Number(o.estimated_value)||0), 0))}
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[200px]">
                  {(board.columns[stage.key] || []).length === 0 && (
                    <div className="text-xs text-slate-400 italic px-2 py-4 text-center">Drop here</div>
                  )}
                  {(board.columns[stage.key] || []).map(opp => (
                    <div
                      key={opp.id}
                      draggable
                      onDragStart={() => setDragging(opp)}
                      onDragEnd={() => setDragging(null)}
                      data-testid={`kanban-card-${opp.id}`}
                      className={`rounded border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing ${dragging?.id === opp.id ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical size={14} className="text-slate-400 mt-0.5 flex-shrink-0"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{opp.title}</div>
                          <Link to={`/staff/bd/organizations/${opp.organization_id}`} className="text-xs text-slate-600 hover:text-[var(--cod-red)] truncate block">{opp.organization?.name || "—"}</Link>
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {opp.estimated_value && <span className="text-xs font-semibold text-emerald-700">{money(opp.estimated_value)}</span>}
                            {opp.priority && opp.priority !== "medium" && (
                              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${PRIORITY_COLORS[opp.priority]}`}>{opp.priority}</span>
                            )}
                            {opp.deadline_at && <span className="text-[10px] text-slate-500">📅 {String(opp.deadline_at).slice(0,10)}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CreateOppDialog open={showCreate} onOpenChange={setShowCreate} presetCategory={category} onCreated={load}/>
    </div>
  );
}

function CreateOppDialog({ open, onOpenChange, presetCategory, onCreated, presetOrgId }) {
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({
    organization_id: presetOrgId || "", title: "", category: presetCategory || "employer_deal",
    estimated_value: "", priority: "medium", deadline_at: "", funder_name: "",
  });
  useEffect(() => {
    if (open) {
      bdApi.listOrgs({}).then(setOrgs).catch(()=>setOrgs([]));
      setForm(f => ({ ...f, category: presetCategory || f.category, organization_id: presetOrgId || f.organization_id }));
    }
  }, [open, presetCategory, presetOrgId]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        organization_id: form.organization_id,
        title: form.title,
        category: form.category,
        priority: form.priority,
      };
      if (form.estimated_value) payload.estimated_value = parseFloat(form.estimated_value);
      if (form.deadline_at) payload.deadline_at = form.deadline_at + "T00:00:00Z";
      if (form.funder_name) payload.funder_name = form.funder_name;
      await bdApi.createOpp(payload);
      toast.success("Opportunity created");
      onOpenChange(false); onCreated && onCreated();
      setForm({ organization_id: "", title: "", category: presetCategory || "employer_deal", estimated_value: "", priority: "medium", deadline_at: "", funder_name: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Opportunity</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Organization</Label>
            <Select value={form.organization_id} onValueChange={v=>setForm({...form, organization_id: v})}>
              <SelectTrigger data-testid="new-opp-org"><SelectValue placeholder="Select an organization…"/></SelectTrigger>
              <SelectContent>
                {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Title</Label><Input required value={form.title} onChange={e=>setForm({...form, title: e.target.value})} placeholder="e.g., Q2 2026 CNA Cohort Contract" data-testid="new-opp-title"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v=>setForm({...form, category: v})}>
                <SelectTrigger data-testid="new-opp-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v=>setForm({...form, priority: v})}>
                <SelectTrigger data-testid="new-opp-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Estimated Value ($)</Label><Input type="number" value={form.estimated_value} onChange={e=>setForm({...form, estimated_value: e.target.value})} placeholder="e.g., 45000" data-testid="new-opp-value"/></div>
            <div><Label>Deadline</Label><Input type="date" value={form.deadline_at} onChange={e=>setForm({...form, deadline_at: e.target.value})} data-testid="new-opp-deadline"/></div>
          </div>
          {(form.category === "grant" || form.category === "foundation") && (
            <div><Label>Funder Name</Label><Input value={form.funder_name} onChange={e=>setForm({...form, funder_name: e.target.value})} placeholder="e.g., MacArthur Foundation" data-testid="new-opp-funder"/></div>
          )}
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button type="submit" className="cod-bg-red" data-testid="new-opp-submit">Create Opportunity</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════ PIPELINES ADMIN ═════════════════════
export function BDPipelines({ isAdmin }) {
  const [pipelines, setPipelines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => bdApi.listPipelines({}).then(setPipelines).catch(()=>setPipelines([]));
  useEffect(() => { load(); }, []);

  const byCategory = useMemo(() => {
    const grouped = {};
    pipelines.forEach(p => { grouped[p.category] = grouped[p.category] || []; grouped[p.category].push(p); });
    return grouped;
  }, [pipelines]);

  return (
    <div data-testid="bd-pipelines-view">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-600">Configure the pipelines opportunities move through. Seeded defaults cover the 7 core Institutional Development flows; administrators can add custom pipelines.</p>
        {isAdmin && (
          <Button className="cod-bg-red" onClick={()=>setShowCreate(true)} data-testid="btn-new-pipeline"><Plus size={14} className="mr-1"/> New Pipeline</Button>
        )}
      </div>
      <div className="space-y-6" data-testid="pipelines-list">
        {Object.entries(byCategory).map(([cat, ps]) => (
          <div key={cat}>
            <div className="text-xs uppercase tracking-widest font-semibold text-[var(--cod-red)] mb-2">{CATEGORY_LABELS[cat] || cat}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {ps.map(p => (
                <Card key={p.id} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.stages.length} stages · {p.is_seeded ? "Seeded default" : "Custom"} {p.is_default && "· Default"}</div>
                      </div>
                      {isAdmin && !p.is_seeded && (
                        <Button size="sm" variant="ghost" onClick={async ()=>{ if (window.confirm(`Delete pipeline "${p.name}"?`)) { try { await bdApi.deletePipeline(p.id); toast.success("Pipeline deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } } }} data-testid={`btn-delete-pipe-${p.id}`}><Trash2 size={14} className="text-slate-400 hover:text-red-600"/></Button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-3">
                      {p.stages.map((s, i) => (
                        <React.Fragment key={s.key}>
                          <span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{s.label}</span>
                          {i < p.stages.length - 1 && <ArrowRight size={10} className="text-slate-400"/>}
                        </React.Fragment>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      <CreatePipelineDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load}/>
    </div>
  );
}

function CreatePipelineDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", category: "employer_deal", is_default: false });
  const [stages, setStages] = useState([{ key: "", label: "" }, { key: "", label: "" }]);
  const submit = async (e) => {
    e.preventDefault();
    if (stages.some(s => !s.key || !s.label)) { toast.error("Every stage needs a key and label"); return; }
    try {
      await bdApi.createPipeline({ ...form, stages });
      toast.success("Pipeline created");
      onOpenChange(false); onCreated && onCreated();
      setForm({ name: "", category: "employer_deal", is_default: false });
      setStages([{ key: "", label: "" }, { key: "", label: "" }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Pipeline</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Name</Label><Input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="new-pipe-name"/></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v=>setForm({...form, category: v})}>
              <SelectTrigger data-testid="new-pipe-category"><SelectValue/></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORY_LABELS).map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stages (in order)</Label>
            <div className="space-y-2 mt-2">
              {stages.map((s, i) => (
                <div key={i} className="grid grid-cols-5 gap-2">
                  <Input placeholder="key (e.g., contacted)" value={s.key} onChange={e=>{ const c=[...stages]; c[i].key=e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'_'); setStages(c); }} data-testid={`new-pipe-stage-key-${i}`} className="col-span-2"/>
                  <Input placeholder="Label" value={s.label} onChange={e=>{ const c=[...stages]; c[i].label=e.target.value; setStages(c); }} data-testid={`new-pipe-stage-label-${i}`} className="col-span-2"/>
                  <Button type="button" variant="outline" size="sm" onClick={()=>setStages(stages.filter((_,x)=>x!==i))} disabled={stages.length<=2}>Remove</Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={()=>setStages([...stages, { key:"", label:"" }])} data-testid="new-pipe-add-stage">+ Add stage</Button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_default} onChange={e=>setForm({...form, is_default: e.target.checked})} data-testid="new-pipe-default"/> Make default for this category</label>
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button type="submit" className="cod-bg-red" data-testid="new-pipe-submit">Create Pipeline</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════ ANALYTICS ═════════════════════
function KPI({ label, value, subtext, icon: Icon, testid }) {
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-serif text-slate-900 mt-1">{value}</div>
            {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
          </div>
          {Icon && <div className="rounded-md bg-slate-100 p-2"><Icon size={18} className="text-[var(--cod-navy)]"/></div>}
        </div>
      </CardContent>
    </Card>
  );
}

export function BDAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { bdApi.analytics().then(setD).catch(()=>setD(null)); }, []);
  if (!d) return <div className="text-sm text-slate-500">Loading analytics…</div>;
  const pct = (v) => v == null ? "—" : `${(v*100).toFixed(0)}%`;
  return (
    <div data-testid="bd-analytics-view" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Revenue</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Total Pipeline Value" value={money(d.pipeline_value)} subtext={`${d.pipeline_total} open opps`} icon={TrendingUp} testid="kpi-pipeline-value"/>
          <KPI label="Revenue Won" value={money(d.revenue_won_value)} icon={Award} testid="kpi-revenue-won"/>
          <KPI label="Revenue Pending" value={money(d.revenue_pending_value)} icon={Target} testid="kpi-revenue-pending"/>
          <KPI label="Annual Projection" value={money(d.annual_revenue_projection)} subtext={`~${money(d.monthly_revenue_projection)}/mo`} icon={TrendingUp} testid="kpi-annual-projection"/>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Conversion Rates</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Employer Conversion" value={pct(d.employer_conversion_rate)} testid="kpi-employer-conv"/>
          <KPI label="Foundation Conversion" value={pct(d.foundation_conversion_rate)} testid="kpi-foundation-conv"/>
          <KPI label="Contract Win Rate" value={pct(d.contract_win_rate)} testid="kpi-contract-win"/>
          <KPI label="Grants Awarded / Submitted" value={`${d.grants_awarded} / ${d.grants_submitted}`} testid="kpi-grants"/>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">By Category</CardTitle></CardHeader>
          <CardContent data-testid="analytics-by-category" className="space-y-2">
            {Object.entries(d.by_category || {}).filter(([_,m]) => m.total > 0).map(([cat, m]) => (
              <div key={cat} className="border-b border-slate-100 pb-2 last:border-b-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{CATEGORY_LABELS[cat] || cat}</div>
                  <div className="text-xs text-slate-600">{money(m.pipeline_value)} pipeline · {money(m.won_value)} won</div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{m.total} opps · {m.open} open · {m.won} won · {m.lost} lost {m.conversion_rate != null && `· ${pct(m.conversion_rate)} conversion`}</div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users size={16}/> Staff Workload</CardTitle></CardHeader>
          <CardContent data-testid="analytics-workload" className="space-y-2">
            {(d.workload || []).length === 0 && <div className="text-sm text-slate-500">No assignments yet.</div>}
            {(d.workload || []).map(w => (
              <div key={w.user_id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{w.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{w.role}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">{w.open_opps} open</div>
                  <div className="text-xs text-slate-500">{money(w.pipeline_value)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertCircle size={16} className="text-amber-600"/> Upcoming Deadlines (30d)</CardTitle></CardHeader>
        <CardContent data-testid="analytics-deadlines" className="space-y-2">
          {(d.upcoming_deadlines || []).length === 0 && <div className="text-sm text-slate-500">No deadlines in the next 30 days.</div>}
          {(d.upcoming_deadlines || []).slice(0, 20).map((dl, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-b-0">
              <div>
                <Link to={`/staff/bd/organizations/${dl.organization_id}`} className="font-semibold text-slate-900 hover:text-[var(--cod-red)]">{dl.title}</Link>
                <span className="text-xs text-slate-500 ml-2">· {dl.field}</span>
              </div>
              <div className="text-xs font-semibold text-amber-700">{String(dl.due_at).slice(0,10)}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
